export interface ClarityMetadata {
   projectId: string;
   userId: string;
   sessionId: string;
   pageNum: number;
}

const METADATA_TIMEOUT_MS = 500;

let queueStub: Window['clarity'] | null = null;

declare global {
   interface Window {
      clarity: {
         (event: 'event', action: string): void;
         (event: 'set', key: string, value: string): void;
         (event: 'upgrade', reason: string): void;
         (
            event: 'metadata',
            callback: (data: ClarityMetadata | undefined) => void,
            wait?: boolean,
            recall?: boolean
         ): void;
         q?: unknown[][];
      };
   }
}

export function trackClarityEvent(action: string) {
   clarityOrQueue()?.('event', action);
}

export function trackClarityTag(key: string, value: string) {
   clarityOrQueue()?.('set', key, value);
}

export function upgradeClaritySession(reason: string) {
   clarityOrQueue()?.('upgrade', reason);
}

/**
 * GTM loads Clarity after hydration, so calls made on a page's first render
 * would find no `window.clarity` and be lost. Install Clarity's own queue
 * stub (the one its snippet defines) so it replays them once it loads.
 * Clarity replaces `window.clarity` when it loads, so while the stub is still
 * there Clarity is not running.
 */
function clarityOrQueue(): Window['clarity'] | null {
   if (typeof window === 'undefined') {
      return null;
   }
   if (window.clarity === undefined) {
      const queue = ((...args: unknown[]) => {
         (queue.q ||= []).push(args);
      }) as Window['clarity'];
      queueStub = queue;
      window.clarity = queue;
   }
   return window.clarity;
}

export function getClaritySessionId(): Promise<string | null> {
   if (
      typeof window === 'undefined' ||
      window.clarity === undefined ||
      window.clarity === queueStub
   ) {
      return Promise.resolve(null);
   }

   return new Promise(resolve => {
      const timer = window.setTimeout(() => resolve(null), METADATA_TIMEOUT_MS);
      try {
         window.clarity(
            'metadata',
            data => {
               window.clearTimeout(timer);
               resolve(data?.sessionId ?? null);
            },
            false,
            true
         );
      } catch {
         window.clearTimeout(timer);
         resolve(null);
      }
   });
}
